/**
 * Klasifikátor odkazu videa — JEDINÉ místo, které rozhoduje, jestli uložený odkaz web
 * přehraje (vize doplneni-webu F8.2). Renderer zobrazí, co klasifikátor přijme; admin
 * označí, co odmítne; nic se nezahazuje potichu jinde. Odsud jde i kanonická identita
 * videa (deduplikace) a kanonické adresy vložení — nikdo jiný je neskládá
 * (`node scripts/videa/videa.mjs kod --tree <strom>` hlídá web i ruční rozbor adres).
 *
 * YouTube část rozboru je `getYoutubeVideoId` z `utils` (utils zůstávají bez importů,
 * volá je i synchronizace podcastů); klasifikátor ji obaluje ověřením hostitele a tvaru,
 * takže `https://evil.example/?x=youtu.be/<id>` ani dvanáctiznakové ID neprojdou.
 *
 * Holé jedenáctiznakové ID je platné JEN v polích, která jsou z definice YouTube
 * (`trips.youtubeVideos` ho nese u většiny hodnot) — jinde je to vadný odkaz.
 */
import { getYoutubeVideoId } from "./utils";

export type YoutubeVideoLink = { provider: "youtube"; id: string; short: boolean };
export type InstagramVideoLink = { provider: "instagram"; kind: "reel" | "p"; code: string };
export type VideoLink = YoutubeVideoLink | InstagramVideoLink;

/** Proč odkaz neprošel — admin ho podle toho neoznačuje jinak, ale měřidlo ho vypisuje. */
export type VideoLinkInvalidReason = "not_url" | "unsupported" | "unrecognized";
export type VideoLinkInvalid = { invalid: VideoLinkInvalidReason };

/** `null` = bez odkazu (prázdný vstup) — to NENÍ vadný odkaz. */
export type VideoLinkResult = VideoLink | VideoLinkInvalid | null;

export interface ClassifyOptions {
  /** Pole z definice YouTube: holé jedenáctiznakové ID se bere jako video. */
  youtubeField?: boolean;
}

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const INSTAGRAM_CODE = /^[A-Za-z0-9_-]{5,64}$/;
const YOUTUBE_HOSTS = new Set(["youtube.com", "youtu.be", "youtube-nocookie.com"]);
const INSTAGRAM_KINDS: Record<string, InstagramVideoLink["kind"]> = {
  reel: "reel",
  reels: "reel",
  p: "p",
};

export function isVideoLink(r: VideoLinkResult): r is VideoLink {
  return r !== null && "provider" in r;
}

function bareHost(hostname: string): string {
  return hostname.toLowerCase().replace(/^(www|m)\./, "");
}

function classifyYoutube(url: URL, host: string): VideoLinkResult {
  // `youtube-nocookie.com` má tytéž cesty jako youtube.com; rozbor zná jen youtube.com.
  const normalized =
    host === "youtu.be"
      ? `https://youtu.be${url.pathname}`
      : `https://www.youtube.com${url.pathname}${url.search}`;
  const id = getYoutubeVideoId(normalized);
  if (!id) return { invalid: "unrecognized" };
  // Rozbor hledá 11 znaků kdekoli za prefixem; ID musí být CELÝ segment cesty nebo `v`.
  const segments = url.pathname.split("/");
  if (url.searchParams.get("v") !== id && !segments.includes(id)) {
    return { invalid: "unrecognized" };
  }
  return { provider: "youtube", id, short: segments[1] === "shorts" };
}

function classifyInstagram(url: URL): VideoLinkResult {
  const [, kindRaw, code] = url.pathname.split("/");
  const kind = INSTAGRAM_KINDS[kindRaw ?? ""];
  if (!kind || !code || !INSTAGRAM_CODE.test(code)) return { invalid: "unrecognized" };
  return { provider: "instagram", kind, code };
}

export function classifyVideoLink(
  input: string | null | undefined,
  opts: ClassifyOptions = {},
): VideoLinkResult {
  const raw = (input ?? "").trim();
  if (!raw) return null;
  if (opts.youtubeField && YOUTUBE_ID.test(raw)) {
    return { provider: "youtube", id: raw, short: false };
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return { invalid: "not_url" };
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return { invalid: "not_url" };
  const host = bareHost(url.hostname);
  if (YOUTUBE_HOSTS.has(host)) return classifyYoutube(url, host);
  if (host === "instagram.com") return classifyInstagram(url);
  return { invalid: "unsupported" };
}

/** Pohodlná zkratka: platný odkaz, nebo `null` (vadný i prázdný). */
export function videoLinkOf(
  input: string | null | undefined,
  opts?: ClassifyOptions,
): VideoLink | null {
  const r = classifyVideoLink(input, opts);
  return isVideoLink(r) ? r : null;
}

/** Kanonická identita videa pro deduplikaci — `?si=`, `reel/` × `p/` ani host ji nemění. */
export function videoIdentity(link: VideoLink): string {
  return link.provider === "youtube" ? `youtube:${link.id}` : `instagram:${link.code}`;
}

export function videoProviderName(link: VideoLink): "YouTube" | "Instagram" {
  return link.provider === "youtube" ? "YouTube" : "Instagram";
}

/** Původ, ze kterého vložení Instagramu posílá zprávy (`postMessage`) — signál, že žije. */
export const INSTAGRAM_EMBED_ORIGIN = "https://www.instagram.com";

/**
 * Adresa přehrávače do `iframe` (jen po souhlasu — vkládá ji výhradně `VideoModal`).
 * `origin` (původ stránky) zapne u YouTube hlášení stavu přehrávače zprávami
 * (`enablejsapi`), podle kterého přehrávač webu pozná zablokovaný autoplay.
 */
export function videoEmbedUrl(
  link: VideoLink,
  opts: { autoplay?: boolean; origin?: string } = {},
): string {
  if (link.provider === "instagram") {
    return `${INSTAGRAM_EMBED_ORIGIN}/${link.kind}/${link.code}/embed/`;
  }
  const api = opts.origin ? `enablejsapi=1&origin=${encodeURIComponent(opts.origin)}&` : "";
  return `https://www.youtube.com/embed/${link.id}?${opts.autoplay ? "autoplay=1&" : ""}${api}rel=0`;
}

/** Kanonická stránka videa — hodnota spouštěče i odkaz „Otevřít na Instagramu“. */
export function videoPageUrl(link: VideoLink): string {
  if (link.provider === "instagram") return `https://www.instagram.com/${link.kind}/${link.code}/`;
  return link.short
    ? `https://www.youtube.com/shorts/${link.id}`
    : `https://www.youtube.com/watch?v=${link.id}`;
}

/**
 * Uložený odkaz vyhodnocený pro vykreslení — co spouštěč na webu potřebuje: kanonickou
 * adresu (hodnota `data-video-url`, ne surový vstup), odkaz, identitu a jméno poskytovatele.
 */
export interface VideoTrigger {
  url: string;
  link: VideoLink;
  identity: string;
  provider: "YouTube" | "Instagram";
}

/**
 * Uložený odkaz → spouštěč, nebo `null`. Jedno pravidlo pro všechny plochy: vadný
 * i prázdný odkaz spouštěč nemá (vadný u pole označí admin, měřidlo ho vypíše).
 */
export function videoTrigger(
  input: string | null | undefined,
  opts?: ClassifyOptions,
): VideoTrigger | null {
  const link = classifyVideoLink(input, opts);
  if (!isVideoLink(link)) return null;
  return {
    url: videoPageUrl(link),
    link,
    identity: videoIdentity(link),
    provider: videoProviderName(link),
  };
}

export interface VideoEntriesOptions extends ClassifyOptions {
  /** Vynechat položku, jejíž video (identita) už v seznamu je — první výskyt vyhrává. */
  unique?: boolean;
}

/**
 * Položky seznamu s platným videem, v pořadí vstupu, každá i se spouštěčem. JEDINÁ smyčka
 * „klasifikuj → vynech vadné a prázdné → (volitelně) deduplikuj podle identity“ pro
 * všechny plochy: roll-up země, video inspirace, pořadí v modalu i karty zájezdu.
 */
export function videoEntries<T>(
  items: Iterable<T> | null | undefined,
  urlOf: (item: T) => string | null | undefined,
  opts: VideoEntriesOptions = {},
): Array<{ item: T; trigger: VideoTrigger }> {
  const { unique = false, ...classify } = opts;
  const seen = new Set<string>();
  const out: Array<{ item: T; trigger: VideoTrigger }> = [];
  for (const item of items ?? []) {
    const trigger = videoTrigger(urlOf(item), classify);
    if (!trigger) continue;
    if (unique) {
      if (seen.has(trigger.identity)) continue;
      seen.add(trigger.identity);
    }
    out.push({ item, trigger });
  }
  return out;
}

/**
 * Zarážka pro přepis vloženého videa v těle článku: ověřená identita včetně druhu
 * Instagramu (`youtube:<id>`, `instagram:<reel|p>:<code>`). Nic jiného ze vstupu nenese.
 */
export function videoRef(link: VideoLink): string {
  return link.provider === "youtube" ? `youtube:${link.id}` : `instagram:${link.kind}:${link.code}`;
}

/** Opak `videoRef` — přijme jen přesný tvar, jinak `null`. */
export function parseVideoRef(ref: string | null | undefined): VideoLink | null {
  const parts = (ref ?? "").split(":");
  if (parts[0] === "youtube" && parts.length === 2 && YOUTUBE_ID.test(parts[1])) {
    return { provider: "youtube", id: parts[1], short: false };
  }
  if (
    parts[0] === "instagram" &&
    parts.length === 3 &&
    (parts[1] === "reel" || parts[1] === "p") &&
    INSTAGRAM_CODE.test(parts[2])
  ) {
    return { provider: "instagram", kind: parts[1], code: parts[2] };
  }
  return null;
}

/**
 * Pole odkazu videa ve formulářích adminu — deklarované NA JEDNOM MÍSTĚ. Každé z nich
 * ukazuje sdílenou hlášku vadného odkazu (`VideoLinkWarning`); měřidlo
 * `node scripts/videa/videa.mjs admin` z tohoto seznamu čte, takže nové pole mimo seznam
 * nebo soubor pole bez hlášky padne tam, ne až u redakce.
 *
 * `field` = jméno pole formuláře (u reelů klíč položky `href` v `SocialReelsEditor`).
 */
export const VIDEO_LINK_FIELDS = [
  { field: "href", youtubeField: false, where: "reely destinace, zájezdu a inspirace" },
  { field: "heroVideoUrl", youtubeField: false, where: "promo video destinace" },
  { field: "youtubeVideos", youtubeField: true, where: "videa zájezdu a hotelu" },
  { field: "guideVideoUrl", youtubeField: false, where: "video průvodce u zájezdu" },
  { field: "videos", youtubeField: false, where: "videa inspirace" },
] as const;

export type VideoLinkField = (typeof VIDEO_LINK_FIELDS)[number]["field"];

/** Je hodnota pole z deklarovaného seznamu VADNÝ odkaz? Prázdná hodnota vadná není. */
export function isInvalidVideoLinkValue(
  field: VideoLinkField,
  value: string | null | undefined,
): boolean {
  const def = VIDEO_LINK_FIELDS.find((f) => f.field === field);
  const result = classifyVideoLink(value, { youtubeField: def?.youtubeField ?? false });
  return result !== null && !isVideoLink(result);
}
